(function (host, factory) {
  'use strict';
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (host && host.document) api.boot(host);
})(typeof window === 'undefined' ? null : window, function () {
  'use strict';

  var MAX_AGE_MS = 5 * 60 * 1000;
  var LOW_STOCK_LIMIT = 10;

  function variantId(value) {
    if (typeof value === 'number' && !Number.isSafeInteger(value)) return null;
    if (typeof value !== 'string' && typeof value !== 'number') return null;
    var id = String(value);
    return /^[1-9]\d*$/.test(id) ? id : null;
  }

  function selectedItems(selection) {
    if (!selection || selection.pending !== false || !Array.isArray(selection.items)) return null;
    var totals = Object.create(null);
    for (var index = 0; index < selection.items.length; index += 1) {
      var item = selection.items[index];
      if (!item || !Number.isSafeInteger(item.quantity) || item.quantity < 0) return null;
      if (item.quantity === 0) continue;
      var id = variantId(item.id);
      if (!id) return null;
      totals[id] = (totals[id] || 0) + item.quantity;
      if (!Number.isSafeInteger(totals[id])) return null;
    }
    return Object.keys(totals);
  }

  function fresh(data, now) {
    if (!data || !Number.isSafeInteger(data.renderedAt) || data.renderedAt <= 0) return false;
    var age = now - data.renderedAt * 1000;
    // Permit small clock differences, but never extend a snapshot indefinitely.
    return Number.isFinite(age) && age >= -60000 && age < MAX_AGE_MS;
  }

  function lowStockLabels(data, selection, now) {
    if (!fresh(data, now) || !Array.isArray(data.variants) ||
        typeof data.message !== 'string' || !data.message.trim()) return [];
    var selected = selectedItems(selection);
    if (!selected || !selected.length) return [];
    var variants = Object.create(null);
    for (var index = 0; index < data.variants.length; index += 1) {
      var variant = data.variants[index];
      var id = variant && variantId(variant.id);
      if (!id || variants[id]) return [];
      variants[id] = variant;
    }
    var labels = [];
    for (var itemIndex = 0; itemIndex < selected.length; itemIndex += 1) {
      var stock = variants[selected[itemIndex]];
      if (!stock) return [];
      if (stock.managed !== true || stock.policy !== 'deny' || stock.available !== true ||
          !Number.isSafeInteger(stock.quantity) || stock.quantity <= 0 || stock.quantity > LOW_STOCK_LIMIT) continue;
      if (typeof stock.label !== 'string' || !stock.label.trim()) return [];
      if (labels.indexOf(stock.label) === -1) labels.push(stock.label);
    }
    return labels;
  }

  function stockStatus(data, selection, now) {
    var merchantLowStock = data && data.merchantLowStock === true;
    if (!data || (!merchantLowStock && !fresh(data, now)) || !Array.isArray(data.variants)) return null;
    var selected = selectedItems(selection);
    // A pending signal can also mean a failed bundle integration. Do not turn
    // an incomplete selection into a reassuring availability claim.
    if (!selected || !selected.length) return null;
    var variants = Object.create(null);
    for (var index = 0; index < data.variants.length; index += 1) {
      var variant = data.variants[index];
      var id = variant && variantId(variant.id);
      if (!id || variants[id]) return null;
      variants[id] = variant;
    }
    var labels = [];
    var lowStock = [];
    for (var itemIndex = 0; itemIndex < selected.length; itemIndex += 1) {
      var stock = variants[selected[itemIndex]];
      if (!stock || stock.available !== true || typeof stock.label !== 'string' || !stock.label.trim()) return null;
      if (!merchantLowStock && (stock.managed !== true || stock.policy !== 'deny' ||
          !Number.isSafeInteger(stock.quantity) || stock.quantity <= 0)) return null;
      // Separate variants with the same label would make an exact count ambiguous.
      if (labels.indexOf(stock.label) !== -1) return null;
      labels.push(stock.label);
      if (stock.quantity <= LOW_STOCK_LIMIT) lowStock.push(stock);
    }
    // Owner-confirmed ongoing low supply: use plain copy, never an invented count.
    if (merchantLowStock) {
      if (typeof data.message !== 'string' || !data.message.trim()) return null;
      return { state: 'low', labels: labels, message: data.message, copy: data.message };
    }
    if (!lowStock.length || typeof data.message !== 'string' || !data.message.trim()) return null;
    var template = lowStock.length > 1 ? data.flavorCountMessage :
      (lowStock[0].quantity === 1 ? data.onlyOneMessage : data.onlyManyMessage);
    if (typeof template !== 'string' || !template.trim() ||
        template.indexOf('[quantity]') === -1 || template.indexOf('[flavor]') === -1) return null;
    var details = lowStock.map(function (item) {
      return template.replace(/\[quantity\]|\[flavor\]/g, function (token) {
        return token === '[quantity]' ? String(item.quantity) : item.label;
      });
    });
    return {
      state: 'low',
      labels: lowStock.map(function (item) { return item.label; }),
      message: data.message,
      copy: data.message + ' — ' + details.join(' · ')
    };
  }

  function parseJSON(value) {
    try { return JSON.parse(value || 'null'); } catch (error) { return null; }
  }

  function readBundleSelection(bundle) {
    var cached = bundle.getAttribute('data-hfs-selection');
    if (cached) return parseJSON(cached);
    // Without a ready signal, counters may still be syncing with the bundle app.
    return { items: [], pending: true };
  }

  function createController(element, scope, host) {
    var output = element.querySelector('[data-hush-stock-message]');
    var data = parseJSON(element.getAttribute('data-hush-stock-data'));
    var merchantLowStock = data && data.merchantLowStock === true;
    var state = { revision: -1, source: null, expired: false, selection: null };

    function hide() {
      if (output) {
        output.hidden = true;
        output.textContent = '';
        output.setAttribute('data-hush-stock-state', '');
      }
    }

    function render() {
      if (!output) return;
      var status = state.expired ? null : stockStatus(data, state.selection, host.Date.now());
      var copy = status ? status.copy : '';
      if (output.textContent !== copy) output.textContent = copy;
      output.setAttribute('data-hush-stock-state', status ? status.state : '');
      output.hidden = !copy;
    }

    function update(selection, source) {
      // Keep stock next to Add to cart when the bundle app inserts its selector.
      var form = scope.querySelector('product-form');
      if (form && form.parentNode && !element.contains(form) && element.nextElementSibling !== form) {
        form.parentNode.insertBefore(element, form);
      }
      if (source !== state.source) {
        state.source = source;
        state.revision = -1;
      }
      if (selection && selection.revision !== undefined) {
        if (!Number.isSafeInteger(selection.revision) || selection.revision < 0) {
          state.selection = null;
          hide();
          return;
        }
        if (selection.revision < state.revision) {
          render();
          return;
        }
        state.revision = selection.revision;
      } else if (state.revision >= 0) {
        // An event without ordering cannot overwrite a versioned selection.
        state.selection = null;
        hide();
        return;
      }
      state.selection = selection;
      render();
    }

    function refresh() {
      var bundle = scope.querySelector('[data-hfs]');
      if (bundle) {
        update(readBundleSelection(bundle), bundle);
      } else {
        var input = scope.querySelector('product-form input[name="id"]');
        update({ items: [{ id: input ? input.value : element.getAttribute('data-hush-stock-initial-id'), quantity: 1 }], pending: false }, scope);
      }
    }

    var delay = data && fresh(data, host.Date.now()) ?
      Math.max(0, data.renderedAt * 1000 + MAX_AGE_MS - host.Date.now()) : 0;
    var timer = merchantLowStock ? null : host.setTimeout(function () { state.expired = true; hide(); }, delay);
    refresh();
    return {
      element: element,
      scope: scope,
      refresh: refresh,
      update: update,
      hide: hide,
      expire: function () {
        if (merchantLowStock) { refresh(); return; }
        state.expired = true;
        hide();
      },
      dispose: function () { if (timer !== null) host.clearTimeout(timer); }
    };
  }

  function boot(host) {
    var doc = host.document;
    if (doc.documentElement.hasAttribute('data-hush-stock-listeners')) {
      doc.dispatchEvent(new host.CustomEvent('hush:stock-init'));
      return;
    }
    doc.documentElement.setAttribute('data-hush-stock-listeners', 'true');
    var controllers = [];

    function scan() {
      controllers = controllers.filter(function (controller) {
        if (controller.element.isConnected) return true;
        controller.dispose();
        return false;
      });
      doc.querySelectorAll('[data-hush-stock]').forEach(function (element) {
        if (controllers.some(function (controller) { return controller.element === element; })) return;
        var scope = element.closest('[id^="MainProduct-"]') || element.closest('.shopify-section') || element.parentElement;
        if (scope) controllers.push(createController(element, scope, host));
      });
    }

    doc.addEventListener('hush:selection-change', function (event) {
      scan();
      controllers.forEach(function (controller) {
        // Scope and source checks prevent a second product widget affecting this one.
        var bundle = controller.scope.querySelector('[data-hfs]');
        if (bundle && (event.target === bundle || bundle.contains(event.target))) {
          controller.update(event.detail, bundle);
        }
      });
    });
    doc.addEventListener('change', function (event) {
      controllers.forEach(function (controller) {
        if (!controller.scope.contains(event.target) || controller.scope.querySelector('[data-hfs]')) return;
        // A native variant change hides the previous flavor immediately. Only an
        // authoritative variant-id change can display the new flavor's stock.
        if (event.target.matches('input[name="id"]')) controller.refresh();
        else controller.hide();
      });
    });
    doc.addEventListener('shopify:section:load', scan);
    doc.addEventListener('shopify:section:unload', scan);
    doc.addEventListener('hush:stock-init', scan);
    doc.addEventListener('visibilitychange', function () {
      if (!doc.hidden) controllers.forEach(function (controller) { controller.refresh(); });
    });
    host.addEventListener('pageshow', function (event) {
      if (event.persisted) controllers.forEach(function (controller) { controller.expire(); });
    });
    if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', scan, { once: true });
    else scan();
  }

  return { lowStockLabels: lowStockLabels, stockStatus: stockStatus, createController: createController, boot: boot };
});
